<template>
  <ModuleWithHeading
    v-if="partsItems && partsItems.length > 0"
    id="parts-heading"
    :class="headingClasses"
    :heading-text="computedHeading"
    data-testid="parts-of-set-grid"
  >
    <h3 id="parts-of-set-heading" class="visually-hidden">
      {{ ariaHeading }}
    </h3>
    <div class="m-parts-of-set__parts-grid" data-testid="parts-of-set-grid">
      <ListingCarousel
        size="xsmall"
        :items="partsItems"
        list-aria-id="parts-of-set-heading"
      >
        <!-- eslint-disable vue/no-useless-template-attributes -->
        <template
          v-if="partsItems"
          ref="carouselSlot"
          #default="{ item, index }"
        >
          <ImageAndCaption
            v-bind="item"
            :key="index"
            :index="index"
            :heading-level="3"
            data-testid="parts-of-set-item"
            :caption-key="captionKey"
          >
            <template
              v-if="isManuscript || item.is_deaccessioned"
              #afterMetadata
            >
              <SearchCartouches
                :item="item"
                :heading-level="4"
                :show-manuscript-number="isManuscript"
                :show-parent-tree="false"
              />
            </template>
          </ImageAndCaption>
        </template>
      </ListingCarousel>
    </div>
  </ModuleWithHeading>
</template>

<script>
import ModuleWithHeading from "@/components/atoms/moduleWithHeading/index.vue";

import {
  useLocalization,
  ListingCarousel,
  ImageAndCaption,
  alignImageBaseMixin
} from "@thegetty-private/getty-ui";
import SearchCartouches from "@/components/atoms/searchCartouches/index.vue";

export default {
  name: "PartsOfSet",
  components: {
    SearchCartouches,
    ImageAndCaption,
    ListingCarousel,
    ModuleWithHeading
  },
  mixins: [alignImageBaseMixin],
  props: {
    /**
     * The heading to pass to ModuleWithHeading
     */
    heading: {
      type: String,
      default: undefined
    },
    /**
     * The array of complex object parts to display
     */
    partsItems: {
      type: Array,
      default: () => {
        return [];
      }
    },
    /**
     * Whether we're showing a manuscript or not
     */
    isManuscript: {
      type: Boolean,
      default: false
    },
    /**
     * The title of the manuscript
     */
    manuscriptTitle: {
      type: String,
      default: undefined
    },
    /**
     * Locale key for text to be used as image caption (if applicable)
     */
    captionKey: {
      required: false,
      type: String,
      default: undefined
    }
  },
  setup(props) {
    return {
      ...useLocalization({
        baseLocaleKey: props?.baseLocaleKey
      })
    };
  },
  data() {
    return {
      /**
       * Letting the AlignImageBase know it should use css-grid
       */
      useGrid: true
    };
  },
  computed: {
    /**
     * Translated text to display as an aria heading for the list of parts
     */
    ariaHeading() {
      let translation = this.isManuscript
        ? "manuscript_parts_aria"
        : "parts_aria";
      return this.translatePart("human_made_object_set." + translation);
    },
    /**
     * Translated text to display as the manuscript parts heading
     */
    computedHeading() {
      if (this.isManuscript == false) {
        return this.heading;
      }
      return this.translatePart("human_made_object_set.manuscript_heading", {
        title: this.manuscriptTitle
      });
    },
    /**
     * the classes to apply to the headings
     * @returns {array}
     */
    headingClasses() {
      let classes = ["m-parts-of-set__heading"];
      if (this.partsItems == undefined) {
        return classes;
      }
      if (this.partsItems.length > 3 && this.partsItems.length <= 6) {
        classes.push("m-parts-of-set__heading--with-carousel-sm");
      }
      if (this.partsItems.length > 6) {
        classes.push("m-parts-of-set__heading--with-carousel-lg");
      }
      return classes;
    }
  }
};
</script>
<style lang="scss">
@import "./index";
</style>
